import React from 'react'
import { ChevronRight } from 'lucide-react'
import { cn } from './cn'

export interface AdvancedDisclosureProps {
  /** Toggle label when collapsed, e.g. "顯示進階選項". */
  showLabel: React.ReactNode
  /** Toggle label when expanded, e.g. "收合進階選項". */
  hideLabel: React.ReactNode
  /** Start expanded. Default collapsed. */
  defaultOpen?: boolean
  children: React.ReactNode
  className?: string
}

/**
 * 進階收合（.adv-toggle ＋ .adv-body）：安靜的可展開區，無外框，給「偵錯／診斷」這類平常
 * 不需動的設定（對應 settings v4 prototype 的 advanced 區）。收合時內容仍掛在 DOM 但加
 * `hidden`（保留子元件狀態、a11y 正確）。樣式在 kiao-app.css。
 */
export function AdvancedDisclosure({
  showLabel,
  hideLabel,
  defaultOpen = false,
  children,
  className,
}: AdvancedDisclosureProps) {
  const [open, setOpen] = React.useState(defaultOpen)
  const bodyId = React.useId()

  return (
    <div className={cn('border-t border-border pt-[var(--gap-sm)]', className)}>
      <button
        type="button"
        className="inline-flex items-center gap-1.5 text-sm text-muted transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-[var(--fg-soft)] focus-visible:outline-offset-2"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((value) => !value)}
      >
        <ChevronRight aria-hidden="true" className={cn('size-4 transition-transform', open && 'rotate-90')} />
        {open ? hideLabel : showLabel}
      </button>
      <div id={bodyId} className="mt-[var(--gap-sm)]" hidden={!open}>
        {children}
      </div>
    </div>
  )
}
